// "Image layout" for a ranking share image: Poster Grid or Compact List, each
// a card with a small drawing of the layout so the choice is visible before
// the server renders it. A radio group (arrow keys move between options).
// Rendered inside PosterSharePanel above the size picker, because the layout
// decides which sizes are offered.
import { useRef } from 'react';
import styles from '../TopMovies/TopMovies.module.css';
import own from './ShareLayoutPicker.module.css';

function GridSketch() {
    return (
        <span className={own.sketchGrid} aria-hidden="true">
            {Array.from({ length: 6 }, (_, i) => <span key={i} className={own.poster} />)}
        </span>
    );
}

function ListSketch() {
    return (
        <span className={own.sketchList} aria-hidden="true">
            {[0, 1].map((col) => (
                <span key={col} className={own.column}>
                    {[0, 1, 2, 3].map((row) => (
                        <span key={row} className={own.row}>
                            <span className={own.num} />
                            <span className={own.thumb} />
                            <span className={own.line} />
                        </span>
                    ))}
                </span>
            ))}
        </span>
    );
}

const SKETCHES = { grid: GridSketch, list: ListSketch };

/**
 * @param {{key: string, label: string, sub: string}[]} options
 * @param {string} value
 * @param {(key: string) => void} onChange
 * @param {string} [suggested]  the option to badge as "Suggested" (never selected for the user)
 * @param {string} [hint]       one line under the cards
 */
export default function ShareLayoutPicker({ options, value, onChange, suggested, hint }) {
    const refs = useRef({});
    const onKeyDown = (event) => {
        const step = { ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1 }[event.key];
        if (!step) return;
        event.preventDefault();
        const index = options.findIndex((o) => o.key === value);
        const next = options[(index + step + options.length) % options.length];
        onChange(next.key);
        refs.current[next.key]?.focus();
    };
    return (
        <div>
            <p className={styles.sectionLabel} id="share-layout-label">Image layout</p>
            <div className={own.cards} role="radiogroup" aria-labelledby="share-layout-label" onKeyDown={onKeyDown}>
                {options.map((option) => {
                    const Sketch = SKETCHES[option.key];
                    const checked = option.key === value;
                    return (
                        <button
                            key={option.key}
                            ref={(el) => { refs.current[option.key] = el; }}
                            type="button"
                            role="radio"
                            aria-checked={checked}
                            tabIndex={checked ? 0 : -1}
                            className={own.card}
                            onClick={() => onChange(option.key)}
                        >
                            {Sketch && <Sketch />}
                            <span className={own.text}>
                                <strong>{option.label}</strong>
                                {suggested === option.key && <span className={own.badge}>Suggested</span>}
                                <span className={own.sub}>{option.sub}</span>
                            </span>
                        </button>
                    );
                })}
            </div>
            {hint && <p className={styles.hint} style={{ marginTop: 6 }}>{hint}</p>}
        </div>
    );
}
